/**
 * Drizzle mutations (PowerSync local-first) for the SUPERVISION domain.
 *
 * GOTCHA #15: PowerSync tables are SQLite VIEWS -> NEVER UPSERT.
 * Create = INSERT; edit = UPDATE by id; delete = DELETE (server write-path
 * materializes as SOFT-DELETE: profile and its log survive).
 *
 * Server JSONB columns (disclosure/consent/policy) are stored as
 * TEXT with serialized JSON; connector re-hydrates to object on upload
 * (`normalizeJsonColumns` in base-connector). `user_id` + `organization_id`
 * travel DENORMALIZED (sync rules requirement).
 *
 * NOTE §2e: POLICY is changed via API (`POST /policy`) to be sealed
 * in guardian hash-chain — NOT written through this path. These mutations
 * cover profile lifecycle (creation/label editing/disclosure/deletion).
 */

import { eq } from "drizzle-orm";
import { type PowerSyncDrizzleDB, supervised_profile } from "@custodia/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import { patchRowVerified } from "../powersync/verified-write";
import type { ConsentRecord, Disclosure, Policy } from "./types";

function toJson(value: unknown): string | null {
  return value === null || value === undefined ? null : JSON.stringify(value);
}

export interface CreateSupervisedProfileInput {
  userId: string;
  organizationId?: string | null;
  /**
   * Classroom (school multi-org). Requires `organizationId` = classroom org;
   * write-path re-validates and discards assignments to classrooms of another org.
   */
  aulaId?: string | null;
  name: string;
  deviceLabel?: string | null;
  disclosure: Disclosure;
  consent?: ConsentRecord | null;
  /** Initial policy (empty by default = everything allowed). */
  policy?: Policy | null;
}

export async function createSupervisedProfileMutation(
  db: PowerSyncDrizzleDB,
  input: CreateSupervisedProfileInput,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(supervised_profile).values({
    id,
    user_id: input.userId,
    organization_id: input.organizationId ?? null,
    aula_id: input.aulaId ?? null,
    name: input.name,
    device_label: input.deviceLabel ?? null,
    legal_basis: input.disclosure.basis,
    disclosure: toJson(input.disclosure),
    consent: toJson(input.consent ?? null),
    policy: toJson(input.policy ?? null),
    archived: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export interface UpdateSupervisedProfileInput {
  name?: string;
  deviceLabel?: string | null;
  disclosure?: Disclosure;
  consent?: ConsentRecord | null;
  archived?: number;
}

/**
 * `organizationId` is CURRENT `organization_id` of row (pass it from
 * `SupervisedProfileRecord` already loaded — do not guess): serves as
 * `scope` (§ security note in `powersync/verified-write.ts`). Profile id
 * travels in URL (`/supervised/$profileId`) and a guardian who is also a member
 * of another organization has those profiles synced simultaneously in
 * SQLite; without scope, an id from another organization would land if
 * matching. `null` = personal profile (no organization).
 */
export async function updateSupervisedProfileMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  input: UpdateSupervisedProfileInput,
  organizationId?: string | null,
): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (input.name !== undefined) {
    patch.name = input.name;
  }
  if (input.deviceLabel !== undefined) {
    patch.device_label = input.deviceLabel;
  }
  if (input.disclosure !== undefined) {
    patch.disclosure = toJson(input.disclosure);
    patch.legal_basis = input.disclosure.basis;
  }
  if (input.consent !== undefined) {
    patch.consent = toJson(input.consent);
  }
  if (input.archived !== undefined) {
    patch.archived = input.archived;
  }
  await patchRowVerified(db, supervised_profile, supervised_profile.id, id, patch, {
    scope:
      organizationId === undefined
        ? []
        : [{ column: supervised_profile.organization_id, value: organizationId }],
  });
}

/**
 * Deletion of a profile: local DELETE (write-path converts to server-side
 * soft-delete; guardian log is preserved).
 */
export async function deleteSupervisedProfileMutation(
  db: PowerSyncDrizzleDB,
  id: string,
): Promise<void> {
  await db.delete(supervised_profile).where(eq(supervised_profile.id, id));
}
